import React, { useState, useRef, useEffect } from 'react';
import { useSession } from '../Utils/SessionContext';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS, buildUrl } from '../Utils/ApiConfig';
import Navigation from './Components/Navigation';
import CompanySettings from '../SettingsComponents/CompanySettings';
import CallbackSettings from '../SettingsComponents/CallbackSettings';
import EvaluationSettings from '../SettingsComponents/EvaluationSettings';
import toast from 'react-hot-toast';

const Settings = () => {
  const { user, logout } = useSession();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    callback_talent_assessment: '',
    callback_talent_report: '',
    callback_new_job: '',
    callback_email: '',
    company_name: '',
    company_logo: null,
    evaluation_completed_content: '',
    target_questionnaire_questions: '',
    target_challenge_questions: '',
    target_questionnaire_breadth: '',
    target_challenge_breadth: '',
    target_questionnaire_time: '',
    target_challenge_time: '',
    challenge_type: '',
    countdown_enabled: false,
    evaluation_steps: {
      resume: true,
      kyc: false,
      video_intro: false,
      questionnaire: true,
      challenge: true
    }
  });
  
  // Set document title
  useEffect(() => {
    document.title = "Settings";
  }, []);
  const [logoPreview, setLogoPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [buttonLoading, setButtonLoading] = useState(false);
  
  // Load settings from server when component mounts
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        setLoadError(null);
        
        // Make GET request to the settings endpoint
        const response = await fetch(API_ENDPOINTS.LOAD_SETTINGS, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${user.access_token}`
          }
        });
        
        // Check if the response is ok
        if (!response.ok) {
          const error = new Error(`Server responded with status: ${response.status}`);
          error.status = response.status;
          throw error;
        }
        
        // Parse the response data
        const responseData = await response.json();
        console.log('Settings loaded:', responseData);
        
        // The response is an array with a single object
        const data = responseData[0];
        
        // Update the form data with the loaded settings
        setFormData(prevData => ({
          ...prevData,
          callback_talent_assessment: data.callback_talent_assessment || '',
          callback_talent_report: data.callback_talent_report || '',
          callback_new_job: data.callback_new_job || '',
          callback_email: data.callback_email || '',
          company_name: data.company_name || '',
          evaluation_completed_content: data.evaluation_completed_content || '',
          target_questionnaire_questions: data.evaluation_settings?.target_questionnaire_questions || '',
          target_challenge_questions: data.evaluation_settings?.target_challenge_questions || '',
          target_questionnaire_breadth: data.evaluation_settings?.target_questionnaire_breadth || '',
          target_challenge_breadth: data.evaluation_settings?.target_challenge_breadth || '',
          target_questionnaire_time: data.evaluation_settings?.target_questionnaire_time || '20',
          target_challenge_time: data.evaluation_settings?.target_challenge_time || '40',
          challenge_type: data.evaluation_settings?.challenge_type || 'quiz',
          countdown_enabled: data.evaluation_settings?.countdown_enabled || false,
          evaluation_steps: data.evaluation_settings?.evaluation_steps || {
            resume: true,
            kyc: false,
            video_intro: false,
            questionnaire: true,
            challenge: true
          }
        }));
        
        // If there's a logo URL, set the preview
        if (data.company_logo_url) {
          // Store the URL from the server in the preview
          setLogoPreview(data.company_logo_url);
        }
        
      } catch (err) {
        console.error('Error loading settings:', err);
        
        // If it's a 401 error, it's an expired session - log out and redirect to login
        if (err.status === 401) {
          console.log('Session expired or unauthorized. Redirecting to login page.');
          logout();
          navigate('/login');
          return;
        }
        
        setLoadError('Failed to load settings. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchSettings();
  }, [user, logout, navigate]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  // Validate form data
  const validateForm = () => {
    const newErrors = {};
    
    // URL validation for Talent Assessment callback (if provided)
    if (formData.callback_talent_assessment && !isValidUrl(formData.callback_talent_assessment)) {
      newErrors.callback_talent_assessment = 'Please enter a valid URL';
    }
    
    // URL validation for Talent Report callback (if provided)
    if (formData.callback_talent_report && !isValidUrl(formData.callback_talent_report)) {
      newErrors.callback_talent_report = 'Please enter a valid URL';
    }
    
    // URL validation for New Job callback (if provided)
    if (formData.callback_new_job && !isValidUrl(formData.callback_new_job)) {
      newErrors.callback_new_job = 'Please enter a valid URL';
    }
    
    // Email validation (if provided)
    if (formData.callback_email && !isValidEmail(formData.callback_email)) {
      newErrors.callback_email = 'Please enter a valid email address';
    }
    
    // Number validation for questionnaire questions
    if (formData.target_questionnaire_questions && (isNaN(formData.target_questionnaire_questions) || formData.target_questionnaire_questions < 1 || formData.target_questionnaire_questions > 50)) {
      newErrors.target_questionnaire_questions = 'Please enter a number between 1 and 50';
    }
    
    // Number validation for challenge questions
    if (formData.target_challenge_questions && (isNaN(formData.target_challenge_questions) || formData.target_challenge_questions < 1 || formData.target_challenge_questions > 20)) {
      newErrors.target_challenge_questions = 'Please enter a number between 1 and 20';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // URL validation helper
  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch (e) {
      return false;
    }
  };

  // Email validation helper
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle logo file selection
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        company_logo: file
      });
      
      // Create a preview URL for the selected image
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Handle clicking the logo upload button
  const handleLogoClick = () => {
    fileInputRef.current.click();
  };

  // Handle evaluation steps checkbox changes
  const handleStepChange = (stepKey, checked) => {
    setFormData({
      ...formData,
      evaluation_steps: {
        ...formData.evaluation_steps,
        [stepKey]: checked
      }
    });
  };
  
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setButtonLoading(true);
    
    if (validateForm()) {
      try {
        // Create a FormData object for the submission
        const formDataToSubmit = new FormData();
        
        // Add all the text fields
        formDataToSubmit.append('callback_talent_assessment', formData.callback_talent_assessment);
        formDataToSubmit.append('callback_talent_report', formData.callback_talent_report);
        formDataToSubmit.append('callback_new_job', formData.callback_new_job);
        formDataToSubmit.append('callback_email', formData.callback_email);
        formDataToSubmit.append('company_name', formData.company_name);
        formDataToSubmit.append('evaluation_completed_content', formData.evaluation_completed_content);
        
        // Create evaluation_settings JSON object
        const evaluationSettings = {
          target_questionnaire_questions: formData.target_questionnaire_questions,
          target_challenge_questions: formData.target_challenge_questions,
          target_questionnaire_breadth: formData.target_questionnaire_breadth,
          target_challenge_breadth: formData.target_challenge_breadth,
          target_questionnaire_time: formData.target_questionnaire_time,
          target_challenge_time: formData.target_challenge_time,
          challenge_type: formData.challenge_type,
          countdown_enabled: formData.countdown_enabled,
          evaluation_steps: formData.evaluation_steps
        };
        // Must stringify for FormData - server should parse this back to JSON before saving as JSONB
        formDataToSubmit.append('evaluation_settings', JSON.stringify(evaluationSettings));
        
        // If there's a new logo file, add it to the form data
        if (formData.company_logo && formData.company_logo instanceof File) {
          formDataToSubmit.append('company_logo', formData.company_logo);
          console.log('Including logo file in submission:', formData.company_logo.name);
        }
        
        // Make POST request to save settings with FormData
        const response = await fetch(API_ENDPOINTS.SAVE_SETTINGS, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${user.access_token}`
            // Note: Do not set Content-Type header when sending FormData
            // The browser will automatically set it to multipart/form-data
          },
          body: formDataToSubmit
        });
        
        // Check if the response is ok
        if (!response.ok) {
          const error = new Error(`Server responded with status: ${response.status}`);
          error.status = response.status;
          throw error;
        }
        
        // Parse the response data
        const data = await response.json();
        console.log('Settings saved:', data);
        
        toast.success('Settings saved successfully!');
        
      } catch (error) {
        console.error('Error saving settings:', error);
        
        // If it's a 401 error, it's an expired session - log out and redirect to login
        if (error.status === 401) {
          console.log('Session expired or unauthorized. Redirecting to login page.');
          logout();
          navigate('/login');
          return;
        }
        
        toast.error('Failed to save settings. Please try again.');
      } finally {
        setButtonLoading(false);
      }
    } else {
      setButtonLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-5 pt-[100px]">
      <Navigation title="Settings" showBackButton={false} />
      
      {/* Settings Forms */}
      <div className="max-w-2xl mx-auto">
        {/* Loading state */}
        {loading && (
          <div className="bg-white rounded-lg shadow p-6 mb-6 text-center">
            <p className="text-gray-600">Loading settings...</p>
          </div>
        )}
        
        {/* Load error */}
        {loadError && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="p-3 bg-red-100 text-red-700 rounded-md mb-4">
              {loadError}
            </div>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-500 text-white font-semibold rounded hover:bg-blue-600 transition-colors"
            >
              Retry
            </button>
          </div>
        )}
        
        {/* Company Settings */}
        {!loading && !loadError && (
          <CompanySettings
            formData={formData}
            handleChange={handleChange}
            handleLogoChange={handleLogoChange}
            handleLogoClick={handleLogoClick}
            logoPreview={logoPreview}
            fileInputRef={fileInputRef}
          />
        )}
        
        {/* Callback Settings */}
        {!loading && !loadError && (
          <CallbackSettings
            formData={formData}
            handleChange={handleChange}
            errors={errors}
          />
        )}
        
        {/* Evaluation Settings */}
        {!loading && !loadError && (
          <EvaluationSettings
            formData={formData}
            handleChange={handleChange}
            handleStepChange={handleStepChange}
            errors={errors}
          />
        )}
        
        {/* Submit Button */}
        {!loading && !loadError && (
          <div className="flex justify-end mb-6">
            <button
              onClick={handleSubmit}
              disabled={buttonLoading}
              className={`px-4 py-2 rounded font-semibold transition-colors flex items-center gap-2 ${
                buttonLoading 
                  ? 'bg-blue-400 text-white cursor-not-allowed' 
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              {buttonLoading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              Save Settings
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
